import ButtonPrimary from 'shared/Button/ButtonPrimary';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'service/axios';
import { Order } from 'reduxStore/store/order/class';
import Loader from './Loader';
import { Check, Package, MapPin, Mail } from 'react-feather';

const PaymentSuccess: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState<Order | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        axios
            .get('/customer-order/' + id)
            .then((res) => {
                setOrderData(res?.data);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                return navigate('/404');
            });
        return () => {
            setOrderData(null);
            setIsLoading(true);
        };
    }, []);

    const currency = orderData?.CurrencyMaster?.value || '';
    const shipping = orderData?.shippingAddress;

    const formatMoney = (value?: number | null) => `${currency} ${Number(value ?? 0).toLocaleString('en-IN')}`;

    return (
        <div className="nc-PaymentSuccess">
            <Helmet>
                <title>Order Confirmed | Instaacard</title>
            </Helmet>
            <div className="container relative pt-10 pb-16 lg:pb-24 lg:pt-16">
                {isLoading && <Loader />}

                {!isLoading && (
                    <div className="max-w-3xl mx-auto">
                        {/* Confirmation */}
                        <div className="text-center">
                            <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600">
                                <Check size={40} strokeWidth={3} />
                            </span>
                            <h1 className="mt-6 font-semibold text-3xl sm:text-4xl !leading-[1.2] tracking-tight text-neutral-900 dark:text-neutral-100">
                                Thank you{orderData?.Customer?.firstName ? `, ${orderData.Customer.firstName}` : ''}!
                            </h1>
                            <p className="mt-3 text-base text-neutral-600 dark:text-neutral-300">
                                Your payment was successful and your order is confirmed.
                            </p>

                            {orderData?.orderNumber && (
                                <div className="mt-6 inline-block rounded-2xl border border-neutral-200 dark:border-neutral-700 px-6 py-3">
                                    <span className="block text-xs uppercase tracking-wider text-neutral-500">Order number</span>
                                    <span className="block text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                                        {orderData.orderNumber}
                                    </span>
                                </div>
                            )}

                            {orderData?.Customer?.email && (
                                <p className="mt-5 flex items-center justify-center gap-2 text-sm text-neutral-500">
                                    <Mail size={16} />
                                    Confirmation sent to {orderData.Customer.email}
                                </p>
                            )}
                        </div>

                        {/* Order summary */}
                        {orderData?.OrderedItems?.length ? (
                            <div className="mt-10 rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                                <div className="flex items-center gap-2 px-5 py-4 border-b border-neutral-200 dark:border-neutral-700">
                                    <Package size={18} />
                                    <h2 className="font-semibold">Order summary</h2>
                                </div>

                                <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
                                    {orderData.OrderedItems.map((item) => (
                                        <li key={item.id} className="flex items-start justify-between gap-4 px-5 py-4">
                                            <div className="min-w-0">
                                                <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                                    {item?.ProductMaster?.name}
                                                </p>
                                                <p className="mt-0.5 text-sm text-neutral-500">Qty: {item.quantity}</p>
                                            </div>
                                            <span className="flex-shrink-0 font-medium">
                                                {formatMoney(item.sellingPrice ?? item.price)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="flex items-center justify-between px-5 py-4 bg-neutral-50 dark:bg-neutral-800">
                                    <span className="font-semibold">Total paid</span>
                                    <span className="text-lg font-semibold">{formatMoney(orderData?.totalAmount)}</span>
                                </div>
                            </div>
                        ) : null}

                        {/* Delivery */}
                        {shipping && (
                            <div className="mt-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 p-5">
                                <div className="flex items-center gap-2">
                                    <MapPin size={18} />
                                    <h2 className="font-semibold">Delivering to</h2>
                                </div>
                                <address className="mt-2 not-italic text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                                    {[shipping.address1, shipping.address2, shipping.cityName, shipping.stateName]
                                        .filter(Boolean)
                                        .join(', ')}
                                    <br />
                                    {[shipping.countryName, shipping.zipCode].filter(Boolean).join(' - ')}
                                </address>
                                <p className="mt-3 text-sm text-neutral-500">
                                    We&apos;ll email you tracking details as soon as your INSTAACARD ships.
                                </p>
                            </div>
                        )}

                        {/* Reference + actions */}
                        <p className="mt-6 text-center text-xs text-neutral-400 break-all">Transaction ID: {id}</p>

                        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <ButtonPrimary href="/account-my-order">View my orders</ButtonPrimary>
                            <Link
                                to="/"
                                className="inline-flex items-center justify-center rounded-full border border-neutral-300 dark:border-neutral-600 px-6 py-3 text-sm font-medium transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            >
                                Continue shopping
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
